import { Component, type ErrorInfo, type ReactNode } from "react";
import { t } from "../i18n";
import { report } from "../report";

interface Props {
  children: ReactNode;
  /** Shown so the person can quote it in a report. */
  trace: string | null;
}

interface State {
  message: string | null;
}

/**
 * Stops a render crash from turning the whole thing black.
 *
 * React unmounts the entire tree when a render throws, and inside Discord's
 * iframe the result is an unexplained black rectangle -- which is exactly what
 * people were reporting. This puts something readable there instead, and files
 * the crash where it can be looked up.
 */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { message: null };

  static getDerivedStateFromError(error: unknown): State {
    return { message: error instanceof Error ? error.message : String(error) };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    report({
      where: "render",
      message: `${error.name}: ${error.message}`,
      stack: error.stack ?? "",
      context: info.componentStack?.slice(0, 300) ?? "",
    });
  }

  override render(): ReactNode {
    if (this.state.message === null) return this.props.children;

    return (
      <div className="boot boot--error">
        <h1>{t("crash.title")}</h1>
        <p>{t("crash.body")}</p>
        {this.props.trace && (
          <p className="crash__trace">
            {t("crash.code")}: <strong>{this.props.trace}</strong>
          </p>
        )}
        <button type="button" className="join__go" onClick={() => window.location.reload()}>
          {t("crash.reload")}
        </button>
        <p className="crash__detail">{this.state.message}</p>
      </div>
    );
  }
}
